import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import { withErrorNotification } from "../notifications";

export interface PanelUser {
  user_id: string;
  name: string;
  email: string;
  created_at?: string;
  /** Primary organization, from user_metadata. */
  organization?: string;
  /** Scout memberships only. */
  memberships: { org: string; role: string; product?: string }[];
}

/**
 * Superuser-only surface. Every endpoint here is refused with a 403 for anyone
 * else, so the UI must not mount it for a non-superuser - not for security,
 * which is enforced server-side, but to avoid a wall of failed requests.
 */
export const superuserApi = createApi({
  reducerPath: "superuserApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["SuperUserUser"],
  endpoints: (builder) => ({
    listAllUsers: builder.query<
      { users: PanelUser[]; truncated: boolean },
      void
    >({
      query: () => ({ url: "/superuser/users" }),
      providesTags: [{ type: "SuperUserUser", id: "LIST" }],
      onQueryStarted: withErrorNotification("Failed to list users"),
    }),
    updateUserPermissions: builder.mutation<
      { user: PanelUser },
      { userId: string; organizations: string[] }
    >({
      query: ({ userId, organizations }) => ({
        url: `/superuser/user/${encodeURIComponent(userId)}/permissions`,
        method: "PUT",
        data: { organizations },
      }),
      // Patch the list from the write's own response rather than refetching.
      // Auth0's user search index is eventually consistent, so an immediate
      // refetch returns the pre-write state and looks like nothing happened.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            superuserApi.util.updateQueryData(
              "listAllUsers",
              undefined,
              (draft) => {
                const i = draft.users.findIndex(
                  (u) => u.user_id === data.user.user_id,
                );
                if (i !== -1) draft.users[i] = data.user;
              },
            ),
          );
        } catch (e) {
          // Surfaced to the user by the component; nothing to patch here.
        }
      },
    }),
  }),
});

export const { useListAllUsersQuery, useUpdateUserPermissionsMutation } =
  superuserApi;
